import { BadRequestException, Injectable } from '@nestjs/common';
import { PrismaService } from '@cart/api-data-access';
import { StoreContextService } from '../tenancy/store-context.service';

export type AnalyticsQuery = Record<string, string | undefined>;

/** Ordered — the funnel only reads correctly in this sequence. */
const FUNNEL_STEPS: { event: string; label: string }[] = [
  { event: 'product.view', label: 'Viewed a product' },
  { event: 'cart.add', label: 'Added to cart' },
  { event: 'checkout.start', label: 'Started checkout' },
  { event: 'checkout.complete', label: 'Completed purchase' },
];

const MAX_DAYS = 400;
const DEFAULT_DAYS = 30;

/**
 * Merchant-facing analytics.
 *
 * Reads ONLY the daily rollups, never `audit.activity_log`. At ~3M events/day
 * across 1,000 stores, aggregating raw rows per page load would be the slowest
 * thing in the product and would do that scan on the disk serving checkout.
 *
 * Everything here is scoped to one store and fails closed, same as
 * MerchantAuditService — an analytics endpoint must never fall back to a
 * default store and quietly show someone else's numbers.
 */
@Injectable()
export class MerchantAnalyticsService {
  constructor(
    private readonly prisma: PrismaService,
    private readonly storeContext: StoreContextService
  ) {}

  private storeId(): string {
    const context = this.storeContext.current();

    if (!context.resolved || !context.storeId) {
      throw new BadRequestException('Analytics requires a resolved store (send x-store-id).');
    }

    return context.storeId;
  }

  private days(query: AnalyticsQuery): number {
    const raw = Number(query.days ?? DEFAULT_DAYS);
    return Number.isFinite(raw) ? Math.min(Math.max(Math.floor(raw), 1), MAX_DAYS) : DEFAULT_DAYS;
  }

  /**
   * BIGINT columns (`revenue`, `gmv`) come back from Prisma as JS BigInt, and
   * `JSON.stringify` THROWS on BigInt rather than skipping it — which turns the
   * whole response into a 500. Paise fit in a double comfortably, so Number is
   * safe here.
   */
  private num(value: unknown): number {
    return typeof value === 'bigint' ? Number(value) : Number(value ?? 0);
  }

  /**
   * The funnel, in sessions rather than events.
   *
   * Sessions is the honest denominator: one shopper reloading a product page 40
   * times is one person, and a funnel built on event counts cheerfully reports
   * conversion above 100%.
   */
  async funnel(query: AnalyticsQuery) {
    const storeId = this.storeId();
    const days = this.days(query);

    const rows = await this.prisma.$queryRawUnsafe<{ eventName: string; sessions: bigint }[]>(
      `SELECT "eventName", COALESCE(sum("sessionCount"), 0) AS sessions
         FROM audit.activity_rollup_daily
        WHERE "storeId" = $1 AND "day" >= current_date - $2::int
        GROUP BY "eventName"`,
      storeId,
      days
    );

    const bySession = new Map(rows.map((r) => [r.eventName, this.num(r.sessions)]));
    const top = bySession.get(FUNNEL_STEPS[0].event) ?? 0;

    const steps = FUNNEL_STEPS.map((step, index) => {
      const sessions = bySession.get(step.event) ?? 0;
      const previous = index === 0 ? sessions : bySession.get(FUNNEL_STEPS[index - 1].event) ?? 0;

      return {
        event: step.event,
        label: step.label,
        sessions,
        // Share of the funnel's top, which is what "conversion" means to a
        // merchant, and the step-over-step drop, which is what tells them
        // WHERE to act.
        shareOfTop: top ? Math.round((sessions / top) * 1000) / 10 : 0,
        dropFromPrevious:
          index === 0 || !previous ? 0 : Math.round(((previous - sessions) / previous) * 1000) / 10,
      };
    });

    const completed = bySession.get('checkout.complete') ?? 0;
    const started = bySession.get('checkout.start') ?? 0;

    return {
      days,
      steps,
      conversionRate: top ? Math.round((completed / top) * 1000) / 10 : 0,
      // Deliberately "abandoned checkouts", not "abandoned carts": this counts
      // people who reached checkout and did not finish, which is the group a
      // merchant can actually do something about.
      abandonedCheckouts: Math.max(started - completed, 0),
      abandonmentRate: started ? Math.round(((started - completed) / started) * 1000) / 10 : 0,
    };
  }

  /**
   * Per product: views → adds → purchases, with revenue taken from the order
   * table rather than from a browser event, so it reconciles with the books.
   *
   * Separates the two failures a sales report cannot tell apart: plenty of
   * views and no sales (price, photos, stock) versus no views at all
   * (discovery). Those need opposite responses.
   */
  async products(query: AnalyticsQuery) {
    const storeId = this.storeId();
    const days = this.days(query);
    const take = Math.min(Math.max(Number(query.take ?? 20), 1), 100);

    const rows = await this.prisma.$queryRawUnsafe<
      {
        productId: string;
        name: string | null;
        views: number;
        addToCarts: number;
        purchases: number;
        revenue: bigint;
        inStock: number | null;
      }[]
    >(
      `SELECT s."productId",
              p."name",
              sum(s."views")::int       AS views,
              sum(s."addToCarts")::int  AS "addToCarts",
              sum(s."purchases")::int   AS purchases,
              COALESCE(sum(s."revenue"), 0) AS revenue,
              i."quantity"              AS "inStock"
         FROM audit.product_stats_daily s
         LEFT JOIN products.products  p ON p."id" = s."productId"
         LEFT JOIN products.inventory i ON i."productId" = s."productId"
        WHERE s."storeId" = $1 AND s."day" >= current_date - $2::int
        GROUP BY s."productId", p."name", i."quantity"
        ORDER BY views DESC
        LIMIT $3::int`,
      storeId,
      days,
      take
    );

    return {
      days,
      items: rows.map((r) => {
        const views = this.num(r.views);
        const purchases = this.num(r.purchases);

        return {
          productId: r.productId,
          title: r.name ?? '(deleted product)',
          views,
          addToCarts: this.num(r.addToCarts),
          purchases,
          revenue: this.num(r.revenue),
          inStock: r.inStock === null ? null : this.num(r.inStock),
          conversionRate: views ? Math.round((purchases / views) * 1000) / 10 : 0,
          // The actionable label. Stock is part of it because "lots of views,
          // no sales" reads very differently when the answer is simply that it
          // was out of stock — and GA could never know that.
          signal: this.productSignal(views, purchases, r.inStock === null ? null : this.num(r.inStock)),
        };
      }),
    };
  }

  private productSignal(views: number, purchases: number, inStock: number | null): string {
    if (inStock !== null && inStock <= 0 && views > 0) return 'out-of-stock';
    if (views === 0) return 'not-found';
    if (views >= 20 && purchases === 0) return 'not-converting';
    if (purchases > 0) return 'selling';
    return 'low-traffic';
  }

  /**
   * What shoppers searched for and did not find.
   *
   * The highest-value, lowest-effort thing on this page: it is demand the
   * merchant is failing to meet, and the fix is usually stocking one item or
   * adding one synonym.
   */
  async searches(query: AnalyticsQuery) {
    const storeId = this.storeId();
    const days = this.days(query);

    const rows = await this.prisma.$queryRawUnsafe<
      { term: string; searchCount: number; zeroResults: number }[]
    >(
      `SELECT "term", sum("searchCount")::int AS "searchCount", sum("zeroResults")::int AS "zeroResults"
         FROM audit.search_rollup_daily
        WHERE "storeId" = $1 AND "day" >= current_date - $2::int
        GROUP BY "term"
        HAVING sum("searchCount") > 0
        ORDER BY sum("zeroResults") DESC, sum("searchCount") DESC
        LIMIT 25`,
      storeId,
      days
    );

    return {
      days,
      items: rows.map((r) => ({
        term: r.term,
        searchCount: this.num(r.searchCount),
        zeroResults: this.num(r.zeroResults),
        /** Searched for, found nothing — demand this shop is not meeting. */
        unmetDemand: this.num(r.zeroResults) > 0,
      })),
    };
  }

  /** Sessions and orders per day — the one time series this page owns. */
  async trend(query: AnalyticsQuery) {
    const storeId = this.storeId();
    const days = this.days(query);

    const rows = await this.prisma.$queryRawUnsafe<
      { day: Date; sessions: number; orders: number; gmv: bigint }[]
    >(
      `SELECT "day", "sessions", "orders", "gmv"
         FROM platform.merchant_stats_daily
        WHERE "storeId" = $1 AND "day" >= current_date - $2::int
        ORDER BY "day" ASC`,
      storeId,
      days
    );

    return {
      days,
      points: rows.map((r) => ({
        day: r.day,
        sessions: this.num(r.sessions),
        orders: this.num(r.orders),
        revenue: this.num(r.gmv),
      })),
    };
  }

  /** Header numbers. Cheap enough to serve alongside any of the above. */
  async summary(query: AnalyticsQuery) {
    const funnel = await this.funnel(query);
    const storeId = this.storeId();
    const days = this.days(query);

    const [totals] = await this.prisma.$queryRawUnsafe<
      { sessions: bigint; orders: bigint; gmv: bigint }[]
    >(
      `SELECT COALESCE(sum("sessions"), 0) AS sessions,
              COALESCE(sum("orders"), 0)   AS orders,
              COALESCE(sum("gmv"), 0)      AS gmv
         FROM platform.merchant_stats_daily
        WHERE "storeId" = $1 AND "day" >= current_date - $2::int`,
      storeId,
      days
    );

    const orders = this.num(totals?.orders);
    const revenue = this.num(totals?.gmv);

    return {
      days,
      sessions: this.num(totals?.sessions),
      orders,
      revenue,
      averageOrderValue: orders ? Math.round(revenue / orders) : 0,
      conversionRate: funnel.conversionRate,
      abandonmentRate: funnel.abandonmentRate,
    };
  }
}
